import React, { useState, useEffect } from "react";
import { styled } from '@mui/material/styles';
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Card,
    Collapse,
    IconButton,
    Grid2,
    Slider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Chip
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast, Bounce } from "react-toastify";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
// import TrendingDownIcon from "@mui/icons-material/TrendingDown";
// import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { useTheme } from '@mui/material/styles';
// import { areaElementClasses } from '@mui/x-charts/LineChart';
import { fetchOptionChainData, placeOrder, positionSizeCalc } from "../services/api";
import { formatIndianCurrency, formatIndianNumber } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';

// toast.configure();


// function AreaGradient({ color, id }) {
//     return (
//         <defs>
//             <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
//                 <stop offset="0%" stopColor={color} stopOpacity={0.3} />
//                 <stop offset="100%" stopColor={color} stopOpacity={0} />
//             </linearGradient>
//         </defs>
//     );
// }

const PlaceOrder = ({ broker, userAccountId }) => {
    // const [index, setIndex] = useState("");
    const { lotSize, defaultStopLossPercentage, defaultTargetPercentage, autoRefreshInterval: defaultAutoRefreshInterval } = useSettings();
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(defaultAutoRefreshInterval);

    const [callOptions, setCallOptions] = useState([]);
    const [putOptions, setPutOptions] = useState([]);
    const [underlyingSpotPrice, setUnderlyingSpotPrice] = useState(0.0);
    const [indexAndExpiry, setIndexAndExpiry] = useState(0);

    const indexAndExpiryOptions = [
        { "id": 1, "indexName": "NIFTY", "expiryDate": "2025-09-09" },
        { "id": 2, "indexName": "BANKNIFTY", "expiryDate": "2025-09-30" }
    ];

    const [optionType, setOptionType] = useState('CALL');
    const [selectedInstrumentId, setSelectedInstrumentId] = useState(null);
    const [selectedInstrument, setSelectedInstrument] = useState(null);
    const [orderType, setOrderType] = useState('market'); // 'market' or 'limit'
    const [price, setPrice] = useState("");
    const [quantityLots, setQuantityLots] = useState(1);
    const [stopLossTrailingStrategyVersion, setStopLossTrailingStrategyVersion] = useState("3");
    const [stopLossTrailingFactor, setStopLossTrailingFactor] = useState("0");

    const [loading, setLoading] = useState(false);
    const [positionCalculationLoading, setPositionCalculationLoading] = useState(false);

    const [maxQuantityByMargin, setMaxQuantityByMargin] = useState(0);
    const [maxQuantityByRisk, setMaxQuantityByRisk] = useState(0);
    const [stopLossPrice, setStopLossPrice] = useState("");
    const [targetPrice, setTargetPrice] = useState("");
    const [positionSizingData, setPositionSizingData] = useState(null);

    useEffect(() => {
        fetchOptionChain();
        if (autoRefreshInterval == 0) {
            return;
        }
        const intervalId = setInterval(() => {
            fetchOptionChain();
        }, autoRefreshInterval * 1000);

        return () => {
            clearInterval(intervalId);
        }
    }, [autoRefreshInterval, indexAndExpiry]);

    // Balance management moved to backend; no local balance refresh

    useEffect(() => {
        let instrument = getInstrumentFromOptionChain(optionType, selectedInstrumentId);
        setSelectedInstrument(instrument);

        // If no instrument found with current ID, fallback to ATM (strike level 0)
        if (!instrument && callOptions.length > 0 && putOptions.length > 0) {
            instrument = getInstrumentFromOptionChain(optionType, null, 0); // ATM fallback
            setSelectedInstrument(instrument);
            if (instrument) {
                setSelectedInstrumentId(instrument.id);
            }
        }
    }, [selectedInstrumentId, optionType, callOptions, putOptions]);

    // Initialize with ATM instrument when option chain data is first loaded
    useEffect(() => {
        if (!selectedInstrumentId && callOptions.length > 0 && putOptions.length > 0) {
            const atmInstrument = getInstrumentFromOptionChain(optionType, null, 0); // ATM
            if (atmInstrument) {
                setSelectedInstrumentId(atmInstrument.id);
            }
        }
    }, [callOptions, putOptions, selectedInstrumentId, optionType]);

    // Fetch position sizing data from server
    useEffect(() => {
        const fetchPositionSizing = async () => {
            try {
                if (!selectedInstrument || !stopLossPrice || !userAccountId) {
                    setPositionSizingData(null);
                    setMaxQuantityByMargin(0);
                    setPositionCalculationLoading(false);
                    return;
                }

                // For market orders, use LTP; for limit orders, use the set price
                const entryPrice = orderType === 'market' ? selectedInstrument.ltp : parseFloat(price);

                if (!entryPrice) {
                    setPositionSizingData(null);
                    setMaxQuantityByMargin(0);
                    setPositionCalculationLoading(false);
                    return;
                }

                setPositionCalculationLoading(true);

                const payload = {
                    user_account_id: userAccountId,
                    instrument_id: parseInt(selectedInstrument.id),
                    price: entryPrice,
                    stop_loss_price: parseFloat(stopLossPrice),
                    target_price: targetPrice ? parseFloat(targetPrice) : undefined,
                };
                const response = await positionSizeCalc(payload);
                const data = response.data;
                setPositionSizingData(data);

                // Set maximum quantity based on available margin
                const maxQuantity = data.max_quantity_by_available_margin || 0;
                setMaxQuantityByMargin(maxQuantity);

                // Set maximum quantity based on risk
                const maxRiskQuantity = data.max_quantity_by_risk || 0;
                setMaxQuantityByRisk(maxRiskQuantity);

                // Set quantity lots to max risk quantity initially, or reset if exceeds maximum
                if (maxRiskQuantity > 0) {
                    setQuantityLots(Math.min(maxRiskQuantity, maxQuantity));
                } else if (quantityLots > maxQuantity) {
                    setQuantityLots(maxQuantity > 0 ? Math.min(maxQuantity, 1) : 1);
                }
                if (maxQuantity == 0) {
                    setQuantityLots(0);
                }
            } catch (error) {
                console.error('Error fetching position sizing:', error);
                setPositionSizingData(null);
                setMaxQuantityByMargin(0);
                setMaxQuantityByRisk(0);
            } finally {
                setPositionCalculationLoading(false);
            }
        };
        fetchPositionSizing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstrument, orderType, price, stopLossPrice, targetPrice, userAccountId]);

    // Auto-populate price, stop loss, and target price when instrument changes
    useEffect(() => {
        if (selectedInstrument && selectedInstrument.ltp) {
            const ltp = selectedInstrument.ltp;

            // Set entry price
            setPrice(ltp.toString());

            // Calculate and set stop loss price
            const stopLossPrice = ltp * (1 + defaultStopLossPercentage / 100);
            setStopLossPrice(stopLossPrice.toFixed(2));

            // Calculate and set target price
            const targetPrice = ltp * (1 + defaultTargetPercentage / 100);
            setTargetPrice(targetPrice.toFixed(2));
        }
    }, [selectedInstrument, defaultStopLossPercentage, defaultTargetPercentage]);

    // Calculate stop loss price based on percentage
    const calculateStopLossPrice = (percentage) => {
        const entryPrice = orderType === 'market' ? selectedInstrument?.ltp : parseFloat(price);
        if (!entryPrice) return;

        const stopLossPrice = entryPrice * (1 - percentage / 100);
        setStopLossPrice(stopLossPrice.toFixed(2));
    };

    // Calculate target price based on percentage
    const calculateTargetPrice = (percentage) => {
        const entryPrice = orderType === 'market' ? selectedInstrument?.ltp : parseFloat(price);
        if (!entryPrice) return;

        const targetPrice = entryPrice * (1 + percentage / 100);
        setTargetPrice(targetPrice.toFixed(2));
    };



    const showToast = (type, message) => {
        let properties = {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
        }
        switch (type) {
            case "success":
                toast.success(message, properties);
                break;
            case "error":
                toast.error(message, properties);
                break;
            case "warning":
                toast.warn(message, properties);
                break;
            default:
                toast.info(message, properties);
        }
    }

    const theme = useTheme();
    const trendColors = {
        up:
            theme.palette.mode === 'light'
                ? theme.palette.success.main
                : theme.palette.success.dark,
        down:
            theme.palette.mode === 'light'
                ? theme.palette.error.main
                : theme.palette.error.dark,
        neutral:
            theme.palette.mode === 'light'
                ? theme.palette.grey[400]
                : theme.palette.grey[700],
    };

    const labelColors = {
        up: 'success',
        down: 'error',
        neutral: 'default',
    };

    const color = labelColors['up'];
    const chartColor = trendColors['up'];

    const fetchOptionChain = () => {
        fetchOptionChainData(indexAndExpiryOptions[indexAndExpiry].indexName, indexAndExpiryOptions[indexAndExpiry].expiryDate)
            .then((response) => {
                setUnderlyingSpotPrice(response.data.underlying_spot_price)
                setCallOptions(response.data.call_options)
                setPutOptions(response.data.put_options)
            })
            .catch((err) => {
                showToast("error", "Error fetching instruments!\n" + err.message);
            });
    }

    const getInstrumentFromOptionChain = (type, instrumentId, strikeLevel = null) => {
        let optionInstruments = [];
        if (type == "CALL") {
            optionInstruments = callOptions;
        }
        else if (type == "PUT") {
            optionInstruments = putOptions;
        }
        else {
            console.error("Invalid Order Type!");
            showToast("error", "Invalid Order Type!");
            return null;
        }

        let instrument = null;

        // First, try to find by instrument ID if provided
        if (instrumentId) {
            for (let i = 0; i < optionInstruments.length; i++) {
                const inst = optionInstruments[i];
                if (inst.id === instrumentId) {
                    instrument = inst;
                    break;
                }
            }
        }

        // If not found by ID, try to find by strike level (fallback)
        if (!instrument && strikeLevel !== null) {
            for (let i = 0; i < optionInstruments.length; i++) {
                const inst = optionInstruments[i];
                if (inst.strike_level == strikeLevel) {
                    instrument = inst;
                    break;
                }
            }
        }

        return instrument;
    }

    // Local calculation removed (server provides quantity and projections)



    // Place a new order
    const handlePlaceOrder = () => {
        setLoading(true);

        let instrument = selectedInstrument;
        if (instrument == null) {
            console.error("Instrument not found: selectedInstrumentId = ", selectedInstrumentId);
            showToast("error", "Instrument not found!");
            setLoading(false);
            return;
        }

        if (!stopLossPrice || !targetPrice || quantityLots <= 0) {
            showToast("error", "Please enter valid stop loss, target price, and quantity!");
            setLoading(false);
            return;
        }

        if (orderType === 'limit' && !price) {
            showToast("error", "Please enter valid limit price!");
            setLoading(false);
            return;
        }


        let orderDetails = {
            broker: broker,
            user_account_id: userAccountId,
            instrument_id: parseInt(instrument.id),
            quantity_lots: quantityLots,
            stop_loss_price: parseFloat(stopLossPrice),
            target_price: parseFloat(targetPrice),
            stop_loss_trailing_strategy_version: parseInt(stopLossTrailingStrategyVersion),
            stop_loss_trailing_factor: parseFloat(stopLossTrailingFactor),
        };

        // Only include price for limit orders
        if (orderType === 'limit') {
            orderDetails.price = parseFloat(price);
        }

        placeOrder(orderDetails)
            .then(() => {
                showToast("success", `Trade entered successfully!`);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error placing order:", error);
                showToast("error", "Error placing order!");
                setLoading(false);
            });
    };

    return (
        <Card sx={{
            // display: { xs: 'none', md: 'block' },
            backgroundColor: 'background.paper',
            p: 2
        }}>
            <Typography variant="h6">Place Order</Typography>
            <Grid2 container spacing={2} sx={{ my: 1 }}>
                <Grid2 size={5}>
                    {/* <InputLabel>Index</InputLabel> */}
                    <Select fullWidth value={indexAndExpiry} onChange={(e) => setIndexAndExpiry(e.target.value)}>
                        {indexAndExpiryOptions.map(option => {
                            return (<MenuItem key={option.id} value={indexAndExpiryOptions.indexOf(option)}>{option.indexName} ({moment(option.expiryDate).format("D MMM")})</MenuItem>)
                        })}
                    </Select>
                </Grid2>
                <Grid2 size={5}>
                    {/* <InputLabel>Refresh Interval</InputLabel> */}
                    <Select
                        fullWidth
                        value={autoRefreshInterval}
                        onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                    >
                        <MenuItem value={0}>Auto Refresh Disabled</MenuItem>
                        <MenuItem value={10}>10 sec</MenuItem>
                        <MenuItem value={30}>30 sec</MenuItem>
                        <MenuItem value={60}>1 min</MenuItem>
                        <MenuItem value={120}>2 mins</MenuItem>
                        <MenuItem value={600}>10 mins</MenuItem>
                    </Select>
                </Grid2>
                <Grid2 size={2}>
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={fetchOptionChain}
                        fullWidth
                        size="small"
                        color="secondary"
                    >
                        {/* Refresh Chain */}
                    </Button>
                </Grid2>
            </Grid2>
            {/* <Box sx={{ width: '100%', height: 50 }}>
                <SparkLineChart
                    colors={[chartColor]}
                    data={[10, 15, 30, 20, 22]}
                    area
                    showHighlight
                    showTooltip
                    xAxis={{
                        scaleType: 'band',
                        data: ["11:00", "13:00", "14:00", "15:00", "16:00"], // Use the correct property 'data' for xAxis
                    }}
                    sx={{
                        [`& .${areaElementClasses.root}`]: {
                            fill: `url(#area-gradient-10)`,
                        },
                    }}
                >
                    <AreaGradient color={chartColor} id={`area-gradient-`} />
                </SparkLineChart>
            </Box> */}
            <Box component="form" noValidate autoComplete="off" sx={{ display: "flex", flexDirection: "column", gap: 1, }}>
                <Grid2 container spacing={2}>
                    <Grid2 size={12}>
                        <center><Typography variant="h6">{indexAndExpiry.indexName} Spot Price : <b>{formatIndianNumber(underlyingSpotPrice)}</b></Typography></center>
                    </Grid2>
                    <Grid2 size={12}>
                        <center>
                            <ToggleButtonGroup
                                color="primary"
                                value={optionType}
                                exclusive
                                size="small"
                                onChange={(e, value) => { setOptionType(value); }}
                            >
                                <ToggleButton color="success" value="CALL">CALL</ToggleButton>
                                <ToggleButton color="error" value="PUT">PUT</ToggleButton>
                            </ToggleButtonGroup>
                        </center>
                    </Grid2>
                    <Grid2 size={2}>
                    </Grid2>
                    <Grid2 size={8}>
                        <Slider
                            defaultValue={0}
                            value={selectedInstrument?.strike_level || 0}
                            min={-4}
                            max={4}
                            step={1} // Set the step to 1 (can be adjusted)
                            marks={[
                                { value: -4, label: "-4" },
                                { value: -3, label: "-3" },
                                { value: -2, label: "-2" },
                                { value: -1, label: "-1" },
                                { value: 0, label: "ATM" },
                                { value: 1, label: "+1" },
                                { value: 2, label: "+2" },
                                { value: 3, label: "+3" },
                                { value: 4, label: "+4" },
                            ]}
                            size="small"
                            onChangeCommitted={(e, value) => {
                                // Find instrument by strike level and set its ID
                                const instrument = getInstrumentFromOptionChain(optionType, null, value);
                                if (instrument) {
                                    setSelectedInstrumentId(instrument.id);
                                }
                            }}
                        // valueLabelFormat={(value) => value < 0 ? `${Math.abs(value)} ITM` : value > 0 ? `${Math.abs(value)} OTM` : `ATM`} // Optionally format the value label
                        />
                    </Grid2>
                    <Grid2 size={12}>
                        <center><Typography variant="h6">{indexAndExpiry.indexName} Instrument : <b>{selectedInstrument?.index_name} {formatIndianNumber(selectedInstrument?.strike_price)} {selectedInstrument?.option_type}</b></Typography></center>
                    </Grid2>
                    <Grid2 size={12}>
                        <center><Typography variant="">LTP : <b>{selectedInstrument?.ltp}</b></Typography></center>
                    </Grid2>
                    <Grid2 size={6}>
                        {/* <Typography variant="body2">Order Type:</Typography> */}
                        <ToggleButtonGroup
                            color="primary"
                            value={orderType}
                            exclusive
                            size="tiny"
                            onChange={(e, value) => { if (value) setOrderType(value); }}
                            fullWidth
                        >
                            <ToggleButton color="success" value="market">Market</ToggleButton>
                            <ToggleButton color="error" value="limit">Limit</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid2>
                    <Grid2 size={6}>
                        <TextField
                            label="Limit Price (₹)"
                            type="number"
                            variant="outlined"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={orderType !== 'limit'}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <TextField
                            label="Stop Loss Price (₹)"
                            type="number"
                            variant="outlined"
                            value={stopLossPrice}
                            onChange={(e) => setStopLossPrice(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {[2, 4, 8, 16, 25].map((percentage) => (
                                <Chip
                                    key={percentage}
                                    label={`${percentage}%`}
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    clickable
                                    onClick={() => calculateStopLossPrice(percentage)}
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            ))}
                        </Box>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            label="SL %"
                            type="number"
                            variant="outlined"
                            value={selectedInstrument?.ltp ? ((stopLossPrice - selectedInstrument.ltp) / selectedInstrument.ltp * 100).toFixed(2) : 0}
                            fullWidth
                            disabled
                            size="small"
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <TextField
                            label="Target Price (₹)"
                            type="number"
                            variant="outlined"
                            value={targetPrice}
                            onChange={(e) => setTargetPrice(e.target.value)}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {[2, 4, 8, 16, 25].map((percentage) => (
                                <Chip
                                    key={percentage}
                                    label={`${percentage}%`}
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    clickable
                                    onClick={() => calculateTargetPrice(percentage)}
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            ))}
                        </Box>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            label="Tgt %"
                            type="number"
                            variant="outlined"
                            value={selectedInstrument?.ltp ? ((targetPrice - selectedInstrument.ltp) / selectedInstrument.ltp * 100).toFixed(2) : 0}
                            fullWidth
                            disabled
                            size="small"
                        />
                    </Grid2>
                    <Grid2 size={12} sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                            {positionCalculationLoading ? (
                                <Box sx={{
                                    p: 2,
                                    bgcolor: 'info.light',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'info.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="info.dark" sx={{ fontWeight: 'bold' }}>
                                        Calculating position size...
                                    </Typography>
                                </Box>
                            ) : maxQuantityByMargin > 0 ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2">
                                            Quantity: {quantityLots} lots ({quantityLots * lotSize} units)
                                        </Typography>
                                        {maxQuantityByRisk > 0 && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setQuantityLots(maxQuantityByRisk)}
                                                sx={{ ml: 1, fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                            >
                                                Max Safe Qty
                                            </Button>
                                        )}
                                    </Box>
                                    <Slider
                                        value={quantityLots}
                                        min={0}
                                        max={maxQuantityByMargin}
                                        step={1}
                                        onChange={(e, value) => setQuantityLots(value)}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => `${value} lots (${value * lotSize} units)`}
                                        marks={[
                                            { value: 0, label: '' },
                                            { value: 1, label: '1x' },
                                            { value: Math.ceil(maxQuantityByMargin * 0.25), label: `${Math.ceil(maxQuantityByMargin * 0.25)}x` },
                                            { value: Math.ceil(maxQuantityByMargin * 0.5), label: `${Math.ceil(maxQuantityByMargin * 0.5)}x` },
                                            { value: Math.ceil(maxQuantityByMargin * 0.75), label: `${Math.ceil(maxQuantityByMargin * 0.75)}x` },
                                            { value: maxQuantityByMargin, label: `${maxQuantityByMargin}x` },
                                        ].filter((mark, index, array) =>
                                            index === 0 ||
                                            (mark.value > array[index - 1].value && mark.value <= maxQuantityByMargin)
                                        )}
                                        color={maxQuantityByRisk && quantityLots > maxQuantityByRisk ? "error" : "primary"}
                                    />
                                    {maxQuantityByRisk && quantityLots > maxQuantityByRisk && (
                                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                            ⚠️ Warning: Selected quantity exceeds risk-based limit: ({maxQuantityByRisk} lots)
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Box sx={{
                                    p: 2,
                                    bgcolor: 'warning.light',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'warning.main'
                                }}>
                                    <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        ⚠️ Insufficient Margin
                                    </Typography>
                                    <Typography variant="body2" color="warning.dark">
                                        {positionSizingData?.margin_required
                                            ? `Minimum margin required: ${formatIndianCurrency(positionSizingData.margin_required)}`
                                            : 'Insufficient margin available for this trade'
                                        }
                                    </Typography>
                                    {/* <Typography variant="caption" color="warning.dark">
                                        Please add funds to your account or adjust your stop loss/target prices to reduce the required margin.
                                    </Typography> */}
                                </Box>
                            )}
                        </Stack>
                    </Grid2>

                    <Grid2 size={12}>
                        {positionCalculationLoading ? (
                            <Box sx={{
                                p: 2,
                                bgcolor: 'info.light',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'info.main',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="info.dark" sx={{ fontWeight: 'bold' }}>
                                    Calculating position metrics...
                                </Typography>
                            </Box>
                        ) : positionSizingData ? (
                            <Box sx={{
                                p: 1.5,
                                bgcolor: '#fafafa',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                            }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
                                    Position Analysis
                                </Typography>
                                <Grid2 container spacing={1}>
                                    {/* Position Size */}
                                    <Grid2 size={3}>
                                        <Box sx={{
                                            p: 1,
                                            bgcolor: '#f3f4f6',
                                            borderRadius: 0.5,
                                            textAlign: 'center',
                                            border: '1px solid #e5e7eb'
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                                                Quantity
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>
                                                {quantityLots} lot ({quantityLots * lotSize} units)
                                            </Typography>
                                            {/* <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                ({quantityLots}L)
                                            </Typography> */}
                                        </Box>
                                    </Grid2>

                                    {/* Margin Required */}
                                    {positionSizingData.margin_required && (
                                        <Grid2 size={3}>
                                            <Box sx={{
                                                p: 1,
                                                bgcolor: '#fef3cd',
                                                borderRadius: 0.5,
                                                textAlign: 'center',
                                                border: '1px solid #fde68a'
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e' }}>
                                                    Margin Required
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#92400e' }}>
                                                    {formatIndianCurrency(quantityLots * positionSizingData.margin_required)}
                                                </Typography>
                                            </Box>
                                        </Grid2>
                                    )}

                                    {/* Projected Loss */}
                                    {positionSizingData.projected_loss && (
                                        <Grid2 size={3}>
                                            <Box sx={{
                                                p: 1,
                                                bgcolor: '#fee2e2',
                                                borderRadius: 0.5,
                                                textAlign: 'center',
                                                border: '1px solid #fecaca'
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#991b1b' }}>
                                                    Projected Loss
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#991b1b' }}>
                                                    {formatIndianCurrency(positionSizingData.projected_loss * quantityLots)}
                                                </Typography>
                                            </Box>
                                        </Grid2>
                                    )}

                                    {/* Projected Profit */}
                                    {positionSizingData.projected_profit && (
                                        <Grid2 size={3}>
                                            <Box sx={{
                                                p: 1,
                                                bgcolor: '#dcfce7',
                                                borderRadius: 0.5,
                                                textAlign: 'center',
                                                border: '1px solid #bbf7d0'
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#166534' }}>
                                                    Projected Profit
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#166534' }}>
                                                    {formatIndianCurrency(positionSizingData.projected_profit * quantityLots)}
                                                </Typography>
                                            </Box>
                                        </Grid2>
                                    )}

                                    {/* Risk Reward Ratio */}
                                    {positionSizingData.margin_utilisation_percentage && (
                                        <Grid2 size={6}>
                                            <Box sx={{
                                                p: 1,
                                                bgcolor: (positionSizingData.margin_utilisation_percentage * quantityLots) <= 30 ? '#dcfce7' : '#fee2e2',
                                                borderRadius: 0.5,
                                                textAlign: 'center',
                                                border: (positionSizingData.margin_utilisation_percentage * quantityLots) <= 30 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                                                mt: 0.5
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: (positionSizingData.margin_utilisation_percentage * quantityLots) <= 30 ? '#166534' : '#991b1b',
                                                        mr: 1
                                                    }}
                                                >
                                                    Margin Utilisation = {(positionSizingData.margin_utilisation_percentage * quantityLots).toFixed(0)}%
                                                </Typography>
                                                {/* <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: positionSizingData.risk_reward_ratio > 1 ? '#166534' : '#991b1b',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    {positionSizingData.risk_reward_ratio > 1 ? '✓ Good' : '⚠ Poor'}
                                                </Typography> */}
                                            </Box>
                                        </Grid2>
                                    )}
                                    {/* Risk Reward Ratio */}
                                    {positionSizingData.risk_reward_ratio && (
                                        <Grid2 size={6}>
                                            <Box sx={{
                                                p: 1,
                                                bgcolor: positionSizingData.risk_reward_ratio >= 0.7 ? '#dcfce7' : '#fee2e2',
                                                borderRadius: 0.5,
                                                textAlign: 'center',
                                                border: positionSizingData.risk_reward_ratio >= 0.7 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                                                mt: 0.5
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: positionSizingData.risk_reward_ratio >= 0.7 ? '#166534' : '#991b1b',
                                                        mr: 1
                                                    }}
                                                >
                                                    Risk : Reward = {positionSizingData.risk_reward_ratio.toFixed(2)}
                                                </Typography>
                                                {/* <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: positionSizingData.risk_reward_ratio > 1 ? '#166534' : '#991b1b',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    {positionSizingData.risk_reward_ratio > 1 ? '✓ Good' : '⚠ Poor'}
                                                </Typography> */}
                                            </Box>
                                        </Grid2>
                                    )}
                                </Grid2>
                            </Box>
                        ) : price && stopLossPrice ? (
                            <Box sx={{
                                p: 2,
                                bgcolor: 'grey.100',
                                borderRadius: 1,
                                textAlign: 'center'
                            }}>
                                <Typography variant="body2" color="textSecondary">
                                    Enter target price to see position sizing calculations
                                </Typography>
                            </Box>
                        ) : null}
                    </Grid2>
                    {/* TODO: Add these later when we have TSL strategy version and factor */}
                    {/* <Grid2 size={6}>
                        <TextField label="TSL Strategy Version" type="number" variant="outlined" value={stopLossTrailingStrategyVersion} onChange={(e) => setStopLossTrailingStrategyVersion(e.target.value)} fullWidth size="small" />
                    </Grid2>
                    <Grid2 size={6}>
                        <TextField label="TSL Factor" type="number" variant="outlined" value={stopLossTrailingFactor} onChange={(e) => setStopLossTrailingFactor(e.target.value)} fullWidth size="small" />
                    </Grid2> */}
                </Grid2>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems={"center"} sx={{ m: 2, gap: 2, mt: 4 }}>
                {loading ? <center><CircularProgress size={24} /></center> :
                    <>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={loading || positionCalculationLoading || quantityLots <= 0}
                            onClick={() => handlePlaceOrder()}
                            color={optionType == "CALL" ? "success" : "error"}
                        >
                            Place Order
                        </Button>
                    </>
                }
            </Box>
        </Card >
    );
};

export default PlaceOrder;

